import React, { useState, useEffect } from 'react';
import {
  DndContext,
  DragOverlay,
  useDraggable,
  useDroppable,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  rectIntersection,
  pointerWithin,
} from '@dnd-kit/core';
import {
  arrayMove,
  sortableKeyboardCoordinates,
} from '@dnd-kit/sortable';
import './DuelPageDrag.css';
import './DeckBuilderPage.css';
import { loadDeckFromStorage } from '../logic/DuelLogicDrag';
import ZoneSlotDrag from '../components/ZoneSlotDrag';
import DroppableHandBar from '../components/DroppableHandBar';
import DraggableDeckCard from '../components/DraggableDeckCard';
import ExpandableDeckPanel from '../components/ExpandableDeckPanel';
import { playSound } from '../utils/soundPlayer';
import { useParams } from 'react-router-dom';
import { db, doc, getDoc, updateDoc, onSnapshot, serverTimestamp, getDocs, collection } from '../firebase';
import { getAuth } from 'firebase/auth';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import actSound from '../assets/sounds/act.wav';
import { toast } from 'react-toastify';

const DuelDragPage = () => {
  const navigate = useNavigate();
  const actAudio = new Audio(actSound);
  const { t } = useTranslation();
  const [mainDeck, setMainDeck] = useState([]);
  const [extraDeck, setExtraDeck] = useState([]);
  const [sideDeck, setSideDeck] = useState([]);
  const [hand, setHand] = useState([]);
  const [fieldMap, setFieldMap] = useState({});
  const [myGYZ, setMyGYZ] = useState([]);
  const [myBZ, setMyBZ] = useState([]);
  const [pendingToken, setPendingToken] = useState(false);
  
  // Card info modal state
  const [selectedCard, setSelectedCard] = useState(null);
  const [showCardInfoModal, setShowCardInfoModal] = useState(false);
  
  // Dnd-kit drag state
  const [activeId, setActiveId] = useState(null);
  const [activeCard, setActiveCard] = useState(null);
  
  // Configure sensors for drag and drop
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 16, // Increased from 8 to 16 pixels to prevent accidental drags
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );
  
  // Token creation function
  const createToken = () => {
    // Find an unoccupied monster zone
    const monsterZones = ['My MZ1', 'My MZ2', 'My MZ3', 'My MZ4', 'My MZ5', 'My MZ6', 'My MZ7', 'My MZ8', 'My MZ9', 'My MZ10', 'EXMZ1', 'EXMZ2'];
    const availableZone = monsterZones.find(zone => !fieldMap[zone]?.card);
    
    if (availableZone) {
      const tokenCard = {
        id: 'token-' + Date.now(), // Unique ID for token
        name: 'Token',
        type: 'Token',
        isToken: true, // Flag to identify tokens
        card_images: [{ image_url: '/token.png' }], // Use the token image
        position: 'ATK',
        from: 'token',
        isFaceDown: false
      };
      
      const updatedFieldMap = { ...fieldMap };
      updatedFieldMap[availableZone] = {
        card: tokenCard,
        position: 'ATK',
        from: 'token'
      };
      
      setFieldMap(updatedFieldMap);
      console.log('Token created in zone:', availableZone);
      console.log('💡 Note: Tokens are temporary and will be removed if moved to non-monster zones');
    } else {
      console.log('No available monster zone for token');
    }
  };

  // Reset function - put all cards back to deck/extra deck, shuffle, and draw 5
  const handleReset = () => {
    console.log('🔄 Starting reset...');
    
    // Close all panels first
    closeAllPanels();
    
    console.log('Current state:', {
      mainDeck: mainDeck.length,
      extraDeck: extraDeck.length,
      hand: hand.length,
      fieldCards: Object.keys(fieldMap).filter(zone => fieldMap[zone]?.card).length,
      graveyard: graveyard.length,
      banished: banished.length
    });

    // Collect all cards from the field
    const fieldCards = [];
    Object.entries(fieldMap).forEach(([zone, zoneData]) => {
      if (zoneData?.card) {
        console.log('Collecting field card:', zoneData.card.name, 'from zone:', zone);
        
        // Handle XYZ materials - detach them and add individually
        if (zoneData.card?.type?.toLowerCase().includes('xyz') && 
            Array.isArray(zoneData.card.materials) && 
            zoneData.card.materials.length > 0) {
          
          console.log('Detaching XYZ materials from:', zoneData.card.name);
          // Add the main XYZ monster
          fieldCards.push(zoneData.card);
          
          // Add each material individually
          zoneData.card.materials.forEach((material, idx) => {
            const cleanedMaterial = { ...material, isFaceDown: false };
            delete cleanedMaterial.materials; // Remove any nested materials
            fieldCards.push(cleanedMaterial);
            console.log('Added material to collection:', material.name);
          });
        } else {
          // Regular card
        fieldCards.push(zoneData.card);
        }
      }
    });

    // Collect cards from graveyard, banished, and hand
    const gyCards = [...graveyard];
    const bzCards = [...banished];
    const handCards = [...hand];

    console.log('Collected cards:', {
      fieldCards: fieldCards.length,
      gyCards: gyCards.length,
      bzCards: bzCards.length,
      handCards: handCards.length
    });

    // Create new arrays for the updated decks
    let newMainDeck = [...mainDeck];
    let newExtraDeck = [...extraDeck];

    // Helper function to determine if card goes to extra deck
    const isExtraDeckCard = (card) => {
      if (!card || !card.type) {
        console.log('Card missing type:', card?.name, 'Full card:', card);
        return false;
      }
      
      const type = card.type.toLowerCase().trim();
      console.log('Checking card type:', card.name, 'Type:', `"${type}"`);
      
      // Extra deck monster types - check for various possible formats
      const extraDeckPatterns = [
        'fusion monster',
        'synchro monster', 
        'synchro tuner monster',
        'xyz monster',
        'link monster',
        // Also check for abbreviated forms
        'fusion',
        'synchro',
        'xyz',
        'link',
        // Check for variations
        'fusion monster card',
        'synchro monster card',
        'xyz monster card',
        'link monster card'
      ];
      
      // Check if the card type matches any extra deck pattern
      const isExtra = extraDeckPatterns.some(pattern => type.includes(pattern));
      
      // Additional checks for edge cases
      if (type.includes('xyz') && !isExtra) {
        console.log('Found XYZ in type but pattern matching failed:', card.name, 'Type:', type);
        return true;
      }
      
      if (type.includes('synchro') && !isExtra) {
        console.log('Found Synchro in type but pattern matching failed:', card.name, 'Type:', type);
        return true;
      }
      
      if (type.includes('fusion') && !isExtra) {
        console.log('Found Fusion in type but pattern matching failed:', card.name, 'Type:', type);
        return true;
      }
      
      if (type.includes('link') && !isExtra) {
        console.log('Found Link in type but pattern matching failed:', card.name, 'Type:', type);
        return true;
      }
      
      console.log('Is extra deck card:', card.name, 'Result:', isExtra);
      
      return isExtra;
    };

    // Add all collected cards back to their appropriate decks
    const allCollectedCards = [...fieldCards, ...handCards, ...gyCards, ...bzCards];
    
    allCollectedCards.forEach(card => {
      if (isExtraDeckCard(card)) {
        console.log('Adding card to extra deck:', card.name, 'Type:', card.type);
        newExtraDeck.push(card);
      } else {
        console.log('Adding card to main deck:', card.name, 'Type:', card.type);
        newMainDeck.push(card);
      }
    });

    console.log('Cards sorted:', {
      newMainDeck: newMainDeck.length,
      newExtraDeck: newExtraDeck.length
    });

    // Log detailed card processing summary
    console.log('📊 Card Processing Summary:');
    console.log('  Original main deck:', mainDeck.length, 'cards');
    console.log('  Original extra deck:', extraDeck.length, 'cards');
    console.log('  Field cards collected:', fieldCards.length, 'cards');
    console.log('  Hand cards collected:', handCards.length, 'cards');
    console.log('  Graveyard cards collected:', gyCards.length, 'cards');
    console.log('  Banished cards collected:', bzCards.length, 'cards');
    console.log('  Total cards collected:', allCollectedCards.length);
    console.log('  Final main deck size:', newMainDeck.length, 'cards');
    console.log('  Final extra deck size:', newExtraDeck.length, 'cards');

    // Shuffle the main deck
    const shuffledMainDeck = shuffleArray(newMainDeck);

    // Update state atomically to prevent UI glitches
    setMainDeck([]);
    setExtraDeck([]);
    setHand([]);
    setFieldMap({});
    setGraveyard([]);
    setBanished([]);

    // Use setTimeout to ensure state updates are processed before setting new values
    setTimeout(() => {
      // Set the new deck and draw 5 cards
    const drawnCards = shuffledMainDeck.slice(0, 5);
    const remainingDeck = shuffledMainDeck.slice(5);
    
      // Verify we're drawing exactly 5 cards
      if (drawnCards.length !== 5) {
        console.error('❌ RESET ERROR: Expected to draw 5 cards, but got:', drawnCards.length);
        console.error('Shuffled deck size:', shuffledMainDeck.length);
        console.error('Drawn cards:', drawnCards.map(card => card.name));
      }
      
    setMainDeck(remainingDeck);
      setExtraDeck(newExtraDeck);
      setHand(drawnCards);

    console.log('Reset completed:', {
      finalMainDeck: remainingDeck.length,
        finalExtraDeck: newExtraDeck.length,
      drawnCards: drawnCards.length,
        totalCardsProcessed: newMainDeck.length + newExtraDeck.length
    });

    // Detailed summary of card sorting
    console.log('=== RESET SUMMARY ===');
      console.log('Cards that went to MAIN DECK:', newMainDeck.length);
      console.log('Cards that went to EXTRA DECK:', newExtraDeck.length);
    console.log('=====================');
      
      // Verify the reset was successful
      if (remainingDeck.length + newExtraDeck.length + drawnCards.length !== newMainDeck.length + newExtraDeck.length) {
        console.error('❌ RESET ERROR: Card count mismatch!');
        console.error('Expected:', newMainDeck.length + newExtraDeck.length);
        console.error('Actual:', remainingDeck.length + newExtraDeck.length + drawnCards.length);
      } else {
        console.log('✅ RESET SUCCESS: All cards accounted for');
      }
      
      // Additional validation: check hand size
      if (drawnCards.length !== 5) {
        console.error('❌ RESET ERROR: Hand size is incorrect!');
        console.error('Expected hand size: 5');
        console.error('Actual hand size:', drawnCards.length);
        console.error('Hand cards:', drawnCards.map(card => card.name));
      } else {
        console.log('✅ RESET SUCCESS: Hand size is correct (5 cards)');
      }
      
      // Validate the final deck state
      setTimeout(() => {
        validateDeckState();
      }, 100);
    }, 0);
  };

  // Draw function - draw 1 card from deck
  const handleDraw = () => {
    if (mainDeck.length === 0) {
      console.log('⚠️ Cannot draw: deck is empty');
      return;
    }
    
      const drawnCard = mainDeck[0];
      const remainingDeck = mainDeck.slice(1);
      
    console.log('🎴 Drawing card:', drawnCard.name);
    console.log('📊 Cards remaining in deck:', remainingDeck.length);
    
    // Update state
      setHand(prev => [...prev, drawnCard]);
      setMainDeck(remainingDeck);
      
    console.log('✅ Card drawn successfully');
  };

  // Shuffle function - shuffle the current deck
  const handleShuffle = () => {
    if (mainDeck.length === 0) {
      console.log('⚠️ Cannot shuffle: deck is empty');
      return;
    }
    
    console.log('🔀 Shuffling deck with', mainDeck.length, 'cards');
    
    // Create a copy and shuffle
    const shuffledDeck = shuffleArray([...mainDeck]);
    
    // Update the deck state
    setMainDeck(shuffledDeck);
    
    // Force a re-render of any open deck panels to show the new order
    // This ensures the expandable panel displays the shuffled deck correctly
    // and prevents React key conflicts when cards are reordered
    if (showDeckPanel) {
      console.log('🔄 Refreshing deck panel to show shuffled order');
      // Close and reopen the panel to force a refresh
      setShowDeckPanel(false);
      setTimeout(() => {
        setShowDeckPanel(true);
      }, 50);
    }
    
    console.log('✅ Deck shuffled successfully');
    console.log('🔀 First few cards after shuffle:', shuffledDeck.slice(0, 3).map(card => card.name));
  };

  // Utility function to shuffle an array
  const shuffleArray = (array) => {
    if (!Array.isArray(array) || array.length === 0) {
      console.log('⚠️ shuffleArray: Invalid input - not an array or empty');
      return array;
    }
    
    if (array.length === 1) {
      console.log('⚠️ shuffleArray: Array has only 1 element, no shuffling needed');
      return array;
    }
    
    console.log('🔀 shuffleArray: Shuffling array with', array.length, 'elements');
    
    const shuffled = [...array];
    for (let i = shuffled.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
    }
    
    console.log('✅ shuffleArray: Shuffling completed');
    return shuffled;
  };

  // Function to validate deck state and log current status
  const validateDeckState = () => {
    const totalCards = mainDeck.length + extraDeck.length + hand.length + 
                      Object.keys(fieldMap).filter(zone => fieldMap[zone]?.card).length + 
                      graveyard.length + banished.length;
    
    console.log('🔍 Deck State Validation:');
    console.log('  Main Deck:', mainDeck.length, 'cards');
    console.log('  Extra Deck:', extraDeck.length, 'cards');
    console.log('  Hand:', hand.length, 'cards');
    console.log('  Field:', Object.keys(fieldMap).filter(zone => fieldMap[zone]?.card).length, 'cards');
    console.log('  Graveyard:', graveyard.length, 'cards');
    console.log('  Banished:', banished.length, 'cards');
    console.log('  Total:', totalCards, 'cards');
    
    // Check for potential issues
    if (mainDeck.length === 0 && hand.length === 0) {
      console.log('⚠️ Warning: Both deck and hand are empty');
    }
    
    if (Object.keys(fieldMap).filter(zone => fieldMap[zone]?.card).length > 0) {
      console.log('📋 Field cards found:');
      Object.entries(fieldMap).forEach(([zone, zoneData]) => {
        if (zoneData?.card) {
          console.log(`    ${zone}: ${zoneData.card.name}`);
        }
      });
    }
    
    return totalCards;
  };

  // Function to validate that all card keys are unique across the game state
  const validateCardKeys = () => {
    console.log('🔑 Card Key Validation:');
    
    // Collect all card IDs from different locations
    const allCardIds = [];
    
    // Main deck cards
    mainDeck.forEach((card, index) => {
      allCardIds.push({ id: card.id, name: card.name, location: 'mainDeck', index });
    });
    
    // Extra deck cards
    extraDeck.forEach((card, index) => {
      allCardIds.push({ id: card.id, name: card.name, location: 'extraDeck', index });
    });
    
    // Hand cards
    hand.forEach((card, index) => {
      allCardIds.push({ id: card.id, name: card.name, location: 'hand', index });
    });
    
    // Field cards
    Object.entries(fieldMap).forEach(([zone, zoneData]) => {
      if (zoneData?.card) {
        allCardIds.push({ id: zoneData.card.id, name: zoneData.card.name, location: 'field', zone });
      }
    });
    
    // Graveyard cards
    graveyard.forEach((card, index) => {
      allCardIds.push({ id: card.id, name: card.name, location: 'graveyard', index });
    });
    
    // Banished cards
    banished.forEach((card, index) => {
      allCardIds.push({ id: card.id, name: card.name, location: 'banished', index });
    });
    
    // Check for duplicate card IDs
    const cardIdCounts = {};
    allCardIds.forEach(cardInfo => {
      if (!cardIdCounts[cardInfo.id]) {
        cardIdCounts[cardInfo.id] = [];
      }
      cardIdCounts[cardInfo.id].push(cardInfo);
    });
    
    // Report duplicates
    const duplicates = Object.entries(cardIdCounts).filter(([id, cards]) => cards.length > 1);
    if (duplicates.length > 0) {
      console.log('⚠️ Duplicate card IDs found:');
      duplicates.forEach(([id, cards]) => {
        console.log(`  Card ID ${id} (${cards[0].name}) appears in:`, cards.map(c => `${c.location}${c.index !== undefined ? `[${c.index}]` : ''}${c.zone ? `(${c.zone})` : ''}`));
      });
    } else {
      console.log('✅ All card IDs are unique across the game state');
    }
    
    console.log('📊 Total unique card IDs:', Object.keys(cardIdCounts).length);
    console.log('📊 Total card instances:', allCardIds.length);
    
    return { uniqueIds: Object.keys(cardIdCounts).length, totalInstances: allCardIds.length };
  };

  // Function to close all panels
  const closeAllPanels = () => {
    console.log('🔒 Closing all panels');
    setShowDeckPanel(false);
    setShowExtraDeckPanel(false);
    setShowGYPanel(false);
    setShowBZPanel(false);
    setShowXyzPanel(false);
    setXyzMaterials([]);
    setXyzMaterialsZone(null);
  };

  // Function to close specific panel after successful card movement
  const closePanelAfterSuccessfulMove = (sourceType) => {
    console.log('🔒 Closing panel after successful move from:', sourceType);
    
    switch (sourceType) {
      case 'deck':
        setShowDeckPanel(false);
        break;
      case 'extra':
        setShowExtraDeckPanel(false);
        break;
      case 'GY':
        setShowGYPanel(false);
        break;
      case 'BZ':
        setShowBZPanel(false);
        break;
      case 'XYZ':
        setShowXyzPanel(false);
        setXyzMaterialsZone(null);
        break;
      default:
        // Don't close panels for other source types (hand, field)
        console.log('🔒 No panel to close for source type:', sourceType);
        break;
    }
  };

  // Handle drag start
  const handleDragStart = (event) => {
    console.log('🖱️ Drag start event:', event);
    if (!event) return;
    
    const { active } = event;
    if (!active) return;
    
    console.log('🖱️ Active element:', active);
    setActiveId(active.id);
    // Find the card data from the active element's data
    const activeData = active.data.current;
    console.log('🖱️ Active data:', activeData);
    if (activeData && activeData.card) {
      setActiveCard(activeData.card);
      
      // Check if the card is being dragged from a panel
      if (activeData.from === 'deck' || activeData.from === 'extra' || activeData.from === 'GY' || activeData.from === 'BZ') {
        console.log('🖱️ Card dragged from panel:', activeData.from);
        setIsDraggingFromPanel(true);
      }
      
      // Log current state before drag
      console.log('📊 State before drag:', {
        hand: hand.length,
        fieldCards: Object.keys(fieldMap).filter(zone => fieldMap[zone]?.card).length,
        graveyard: graveyard.length,
        banished: banished.length,
        mainDeck: mainDeck.length,
        extraDeck: extraDeck.length
      });
    }
  };

  // ============================================================================
  // DND-KIT DRAG AND DROP IMPLEMENTATION
  // ============================================================================
  // 
  // This implementation centralizes all drag and drop logic in the main component
  // using dnd-kit's onDragEnd event. The approach provides several benefits:
  //
  // 1. **Centralized Logic**: All card movement is handled in one place, preventing
  //    conflicts between multiple drop handlers
  // 2. **Consistent State Updates**: All related state changes happen atomically
  // 3. **Better Validation**: Comprehensive validation before any state changes
  // 4. **Cleaner Components**: Child components only handle visual representation
  //
  // Key dnd-kit concepts used:
  // - DndContext: Wraps the entire drag and drop area
  // - onDragEnd: Main event handler for processing drops
  // - useDraggable: Makes cards draggable with unique IDs
  // - useDroppable: Makes zones droppable with unique IDs
  // - DragOverlay: Provides visual feedback during drag
  // ============================================================================

  // Handle drag end - Main drag and drop logic for card movement
  const handleDragEnd = (event) => {
    console.log('🖱️ Drag end event:', event);
    if (!event) return;
    
    const { active, over } = event;
    
    // Reset panel dragging state
    setIsDraggingFromPanel(false);
    setActiveId(null);
    setActiveCard(null);
    
    // If no drop target or same element, do nothing
    if (!over || !active || active.id === over.id) {
      console.log('🖱️ No valid drop target or same element');
      return;
    }
    
    console.log('🖱️ Card dropped from', active.id, 'to', over.id);
    
    // Extract card data from the active element
    const activeData = active.data.current;
    if (!activeData || !activeData.card) {
      console.log('❌ No card data found in active element');
      return;
    }
    
    // Parse the drop target ID to determine the target zone
    // dnd-kit IDs follow this pattern:
    // - Zone drops: "drop-{zoneLabel}" (e.g., "drop-My MZ1")
    // - Hand drops: "drop-hand"
    let targetZone = null;
    if (over.id === 'drop-hand') {
      targetZone = 'hand';
    } else if (over.id.startsWith('drop-')) {
      targetZone = over.id.replace('drop-', '');
    }
    
    if (!targetZone) {
      console.log('❌ Could not determine target zone from:', over.id);
      
      // Special handling for tokens dropped outside valid zones
      if (activeData.card?.isToken) {
        console.log('🗑️ Token dropped outside valid zones - removing token completely');
        // Remove token from source
        if (activeData.from === 'field' && activeData.fromZone) {
          setFieldMap(prev => {
            const newFieldMap = { ...prev };
            delete newFieldMap[activeData.fromZone].card;
            return newFieldMap;
          });
          console.log('✅ Token removed from game');
        }
      }
      return;
    }
    
    console.log('🎯 Moving card to target zone:', targetZone);
    
    // Handle the card movement based on source and target
    const moveSuccessful = handleCardMovement(activeData, targetZone);
    
    // If the move was successful and the card came from a panel, close the panel
    if (moveSuccessful && activeData.from) {
      closePanelAfterSuccessfulMove(activeData.from);
      
              // Special handling for XYZ panel - close it if no materials left
        if (activeData.from === 'XYZ') {
          if (!xyzMaterialsZone || !fieldMap[xyzMaterialsZone]?.card?.materials || fieldMap[xyzMaterialsZone].card.materials.length === 0) {
            console.log('No XYZ monster with materials found, closing XYZ panel');
            setShowXyzPanel(false);
            setXyzMaterials([]);
            setXyzMaterialsZone(null);
          }
        }
    }
    
    // Log current state after drag
    console.log('📊 State after drag:', {
      hand: hand.length,
      fieldCards: Object.keys(fieldMap).filter(zone => fieldMap[zone]?.card).length,
      graveyard: graveyard.length,
      banished: banished.length,
      mainDeck: mainDeck.length,
      extraDeck: extraDeck.length
    });
  };

  // Centralized card movement logic
  // This function handles all card movement between zones, ensuring consistency
  const handleCardMovement = (sourceData, targetZone) => {
    const { card, from, fromZone, index, cardId } = sourceData;
    
    console.log('🔄 Processing card movement:', {
      card: card?.name,
      cardId: cardId || card?.id,
      from,
      fromZone,
      index,
      targetZone,
      isToken: card?.isToken
    });
    
    // Special handling for tokens - if they can't be moved to target zone, remove them completely
    if (card?.isToken) {
      // Check if the target zone is a valid monster zone for tokens
      const isValidTokenTarget = ['My MZ1', 'My MZ2', 'My MZ3', 'My MZ4', 'My MZ5', 'My MZ6', 'My MZ7', 'My MZ8', 'My MZ9', 'My MZ10', 'EXMZ1', 'EXMZ2'].includes(targetZone);
      
      if (!isValidTokenTarget) {
        console.log('🗑️ Token dropped in invalid location - removing token completely');
        // Remove token from source without adding to target
        removeCardFromSource(sourceData, { ...fieldMap }, [...hand], [...graveyard], [...banished], [...mainDeck], [...extraDeck]);
        // Update state to reflect token removal
        if (from === 'field' && fromZone) {
          setFieldMap(prev => {
            const newFieldMap = { ...prev };
            delete newFieldMap[fromZone].card;
            return newFieldMap;
          });
        }
        console.log('✅ Token removed from game');
        return true; // Indicate successful operation (token removal)
      }
    }
    
    // Validate the move before making any changes
    if (!canMoveCard(sourceData, targetZone)) {
      console.log('❌ Card movement rejected');
      
      // Special handling for tokens - if validation fails, remove them completely
      if (card?.isToken) {
        console.log('🗑️ Token movement validation failed - removing token completely');
        // Remove token from source without adding to target
        removeCardFromSource(sourceData, { ...fieldMap }, [...hand], [...graveyard], [...banished], [...mainDeck], [...extraDeck]);
        // Update state to reflect token removal
        if (from === 'field' && fromZone) {
          setFieldMap(prev => {
            const newFieldMap = { ...prev };
            delete newFieldMap[fromZone].card;
            return newFieldMap;
          });
        }
        console.log('✅ Token removed from game');
        return true; // Indicate successful operation (token removal)
      }
      
      return false;
    }
    
    // Create copies of current state to work with
    // This ensures we don't modify the original state until we're ready
    let newFieldMap = { ...fieldMap };
    let newHand = [...hand];
    let newGraveyard = [...graveyard];
    let newBanished = [...banished];
    let newMainDeck = [...mainDeck];
    let newExtraDeck = [...extraDeck];
    
    // Log the state before any changes
    if (from === 'hand') {
      console.log('📊 Hand state before removal:', {
        totalCards: newHand.length,
        cards: newHand.map((c, i) => `${i}: ${c?.name} (ID: ${c?.id})`),
        removingIndex: index,
        removingCard: card?.name
      });
    }
    
    // Remove card from source location
    // Pass target zone information for proper XYZ material handling
    const sourceDataWithTarget = { ...sourceData, targetZone };
    removeCardFromSource(sourceDataWithTarget, newFieldMap, newHand, newGraveyard, newBanished, newMainDeck, newExtraDeck);
    
    // Log the state after removal
    if (from === 'hand') {
      console.log('📊 Hand state after removal:', {
        totalCards: newHand.length,
        cards: newHand.map((c, i) => `${i}: ${c?.name} (ID: ${c?.id})`)
      });
    }
    
    // Add card to target location
    addCardToTarget(card, targetZone, newFieldMap, newHand, newGraveyard, newBanished, newMainDeck, newExtraDeck);
    
    // Update all states at once to ensure consistency
    // This prevents partial updates that could cause UI inconsistencies
    console.log('✅ Card movement successful, updating states');
    setFieldMap(newFieldMap);
    setHand(newHand);
    setGraveyard(newGraveyard);
    setBanished(newBanished);
    setMainDeck(newMainDeck);
    setExtraDeck(newExtraDeck);
    
            // Update XYZ materials state if a material was moved from the XYZ panel
        if (sourceData.from === 'XYZ') {
          // Find the updated XYZ monster and update the materials state
          if (xyzMaterialsZone && newFieldMap[xyzMaterialsZone]?.card) {
            const xyzMonster = newFieldMap[xyzMaterialsZone].card;
            if (xyzMonster.materials && xyzMonster.materials.length > 0) {
              setXyzMaterials([...xyzMonster.materials]);
              console.log('Updated XYZ materials state:', xyzMonster.materials.map(m => m.name));
            } else {
              // No materials left, clear the state
              setXyzMaterials([]);
              setXyzMaterialsZone(null);
              console.log('Cleared XYZ materials state - no materials left');
            }
          } else {
            // No XYZ monster with materials found, clear the materials state
            setXyzMaterials([]);
            setXyzMaterialsZone(null);
            console.log('Cleared XYZ materials state - no monster with materials found');
          }
        }
    
    return true; // Indicate successful move
  };

  // Validate if a card can be moved to the target zone
  // This function implements the game rules for card placement
  const canMoveCard = (sourceData, targetZone) => {
    const { card, from, fromZone } = sourceData;
    
    // Prevent moving to the same zone
    if (from === 'field' && fromZone === targetZone) {
      console.log('❌ Cannot move to same zone');
      return false;
    }
    
    // Prevent hand-to-hand moves
    if (from === 'hand' && targetZone === 'hand') {
      console.log('❌ Cannot move card within hand');
      return false;
    }
    
    // Handle special cases for different zone types
    if (targetZone === 'hand') {
      // Hand accepts cards from field, graveyard, banished, deck, and extra deck
      // But not from hand itself
      const validSources = ['field', 'GY', 'BZ', 'deck', 'extra'];
      if (!validSources.includes(from)) {
        console.log('❌ Hand cannot accept cards from:', from);
        return false;
      }
      
      // Special validation for tokens - they cannot go to hand
      if (card?.isToken) {
        console.log('❌ Tokens cannot be moved to hand');
        return false;
      }
      
      console.log('✅ Hand can accept card from:', from);
      return true;
    }
    
    // Handle XYZ materials being moved from the XYZ panel
    if (from === 'XYZ') {
      // XYZ materials can be moved to graveyard, banished, or other monster zones
      const validTargets = ['My GYZ', 'My BZ', 'My MZ1', 'My MZ2', 'My MZ3', 'My MZ4', 'My MZ5', 'My MZ6', 'My MZ7', 'My MZ8', 'My MZ9', 'My MZ10', 'EXMZ1', 'EXMZ2'];
      if (validTargets.includes(targetZone)) {
        console.log('✅ XYZ material can be moved to:', targetZone);
        return true;
      } else {
        console.log('❌ XYZ material cannot be moved to:', targetZone);
        return false;
      }
    }
    
    if (targetZone === 'My GYZ') {
      // Graveyard accepts all cards except tokens
      if (card?.isToken) {
        console.log('❌ Tokens cannot be moved to graveyard - they will be removed');
        return false;
      }
      return true;
    }
    
    if (targetZone === 'My BZ') {
      // Banished zone accepts all cards except tokens
      if (card?.isToken) {
        console.log('❌ Tokens cannot be moved to banished zone - they will be removed');
        return false;
      }
      return true;
    }
    
    if (targetZone === 'My Deck') {
      // Deck accepts all cards except tokens
      if (card?.isToken) {
        console.log('❌ Tokens cannot be moved to deck - they will be removed');
        return false;
      }
      return true;
    }
    
    if (targetZone === 'My Extra') {
      // Extra deck accepts all cards except tokens
      if (card?.isToken) {
        console.log('❌ Tokens cannot be moved to extra deck - they will be removed');
        return false;
      }
      return true;
    }
    
    // Spell/Trap zones - only accept cards if empty, and never accept tokens
    if (['My STZ1', 'My STZ2', 'My STZ3', 'My STZ4', 'My STZ5', 'My STZ6', 'My STZ7', 'My STZ8', 'My STZ9', 'My STZ10'].includes(targetZone)) {
      // Only accept if zone is empty and not a token
      if (card?.isToken) {
        console.log('❌ Tokens cannot be moved to spell/trap zones - they will be removed');
        return false;
      }
      return !fieldMap[targetZone]?.card;
    }
    
    // Field Zone - only one field spell at a time, and never accept tokens
    if (targetZone === 'My FZ') {
      if (card?.isToken) {
        console.log('❌ Tokens cannot be moved to field zone - they will be removed');
        return false;
      }
      // Only accept if zone is empty
      return !fieldMap[targetZone]?.card;
    }
    
    // Monster zones - handle XYZ mechanics and token restrictions
    if (['My MZ1', 'My MZ2', 'My MZ3', 'My MZ4', 'My MZ5', 'My MZ6', 'My MZ7', 'My MZ8', 'My MZ9', 'My MZ10', 'EXMZ1', 'EXMZ2'].includes(targetZone)) {
      const targetCard = fieldMap[targetZone]?.card;
      if (targetCard) {
        // Zone is occupied - check if we can add as material or replace
        const isXYZMonster = card?.type?.toLowerCase().includes('xyz');
        const targetIsXYZ = targetCard?.type?.toLowerCase().includes('xyz');
        const isToken = card?.isToken;
        
        // Can add as material to XYZ monster, or replace with XYZ monster
        // Tokens cannot be used as materials
        return (isXYZMonster || targetIsXYZ) && !isToken;
      }
      // Empty monster zone accepts all cards
      return true;
    }
    
    console.log('❌ Target zone not recognized:', targetZone);
    return false;
  };

  // Remove card from source location
  // This function handles the cleanup when a card is moved
  const removeCardFromSource = (sourceData, newFieldMap, newHand, newGraveyard, newBanished, newMainDeck, newExtraDeck) => {
    const { from, fromZone, index, targetZone } = sourceData;
    
    console.log('🗑️ Removing card from source:', { from, fromZone, index, targetZone });
    
    if (from === 'field' && fromZone) {
      const cardToRemove = newFieldMap[fromZone]?.card;
      if (cardToRemove) {
        // Handle XYZ materials when moving monster
        // FIXED: Materials are only sent to graveyard when moving to non-monster zones
        // This prevents material duplication when XYZ monsters are moved between monster zones
        if (cardToRemove?.type?.toLowerCase().includes('xyz') && 
            Array.isArray(cardToRemove.materials) && 
            cardToRemove.materials.length > 0) {
          
          console.log('XYZ monster detected with materials:', {
            monsterName: cardToRemove.name,
            materialCount: cardToRemove.materials.length,
            targetZone: targetZone,
            isMonsterZone: targetZone && ['My MZ1', 'My MZ2', 'My MZ3', 'My MZ4', 'My MZ5', 'My MZ6', 'My MZ7', 'My MZ8', 'My MZ9', 'My MZ10', 'EXMZ1', 'EXMZ2'].includes(targetZone)
          });
          
          // Check if the target zone is a monster zone where materials should stay attached
          if (targetZone && ['My MZ1', 'My MZ2', 'My MZ3', 'My MZ4', 'My MZ5', 'My MZ6', 'My MZ7', 'My MZ8', 'My MZ9', 'My MZ10', 'EXMZ1', 'EXMZ2'].includes(targetZone)) {
            // Moving to another monster zone - keep materials attached
            console.log('XYZ monster moving to monster zone, keeping materials attached');
          } else {
            // Moving to non-monster zone - send materials to graveyard AND remove them from the monster
            console.log('Sending XYZ materials to graveyard and removing from monster (moving to non-monster zone)');
            const materialsToGY = cardToRemove.materials.map(material => {
              const cleanedMaterial = { ...material, isFaceDown: false };
              delete cleanedMaterial.materials;
              return cleanedMaterial;
            });
            newGraveyard.push(...materialsToGY);
            console.log('Materials sent to graveyard:', materialsToGY.map(m => m.name));
            
            // Remove materials from the XYZ monster itself
            cardToRemove.materials = [];
            console.log('Materials removed from XYZ monster - it is now a regular XYZ monster');
          }
        }
        
        delete newFieldMap[fromZone].card;
        console.log('✅ Removed card from field zone:', fromZone);
      }
    } else if (from === 'hand' && index !== undefined) {
      if (index >= 0 && index < newHand.length) {
        // Store the card being removed for better logging and debugging
        const removedCard = newHand[index];
        console.log('🗑️ Removing hand card:', {
          cardName: removedCard?.name,
          cardId: removedCard?.id,
          index,
          totalHandBefore: newHand.length
        });
        
        // Remove the card at the specific index
        newHand.splice(index, 1);
        
        console.log('✅ Removed card from hand:', {
          removedCard: removedCard?.name,
          newHandLength: newHand.length,
          remainingCards: newHand.map((card, i) => `${i}: ${card?.name}`)
        });
      } else {
        console.log('❌ Invalid hand index:', index, 'Hand length:', newHand.length);
      }
    } else if (from === 'XYZ' && index !== undefined) {
      // Handle XYZ materials being dragged from the XYZ panel
      console.log('Removing XYZ material from panel:', { index, targetZone });
      
      // Use the tracked zone to find the correct XYZ monster
      if (xyzMaterialsZone && newFieldMap[xyzMaterialsZone]?.card) {
        const xyzMonster = newFieldMap[xyzMaterialsZone].card;
        console.log('Found XYZ monster with materials:', {
          zone: xyzMaterialsZone,
          monsterName: xyzMonster.name,
          materialCount: xyzMonster.materials.length
        });
        
        // Remove the material at the specified index
        if (index >= 0 && index < xyzMonster.materials.length) {
          const removedMaterial = xyzMonster.materials[index];
          xyzMonster.materials.splice(index, 1);
          
          console.log('Removed material from XYZ monster:', {
            removedMaterial: removedMaterial.name,
            newMaterialCount: xyzMonster.materials.length,
            remainingMaterials: xyzMonster.materials.map(m => m.name)
          });
          
          // If no materials left, the monster becomes a regular XYZ monster
          if (xyzMonster.materials.length === 0) {
            console.log('XYZ monster now has no materials - it is a regular XYZ monster');
          }
        } else {
          console.log('❌ Invalid material index:', index, 'Material count:', xyzMonster.materials.length);
        }
      } else {
        console.log('❌ No XYZ monster found with materials or zone not tracked');
      }
    } else if (from === 'GY' && index !== undefined) {
      if (index >= 0 && index < newGraveyard.length) {
        newGraveyard.splice(index, 1);
        console.log('✅ Removed card from graveyard at index:', index);
      } else {
        console.log('❌ Invalid graveyard index:', index);
      }
    } else if (from === 'BZ' && index !== undefined) {
      if (index >= 0 && index < newBanished.length) {
        newBanished.splice(index, 1);
        console.log('✅ Removed card from banished at index:', index);
      } else {
        console.log('❌ Invalid banished index:', index);
      }
    } else if (from === 'deck' && index !== undefined) {
      if (index >= 0 && index < newMainDeck.length) {
        newMainDeck.splice(index, 1);
        console.log('✅ Removed card from deck at index:', index);
      } else {
        console.log('❌ Invalid deck index:', index);
      }
    } else if (from === 'extra' && index !== undefined) {
      if (index >= 0 && index < newExtraDeck.length) {
        newExtraDeck.splice(index, 1);
        console.log('✅ Removed card from extra deck at index:', index);
      } else {
        console.log('❌ Invalid extra deck index:', index);
      }
    }
  };

  // Add card to target location
  // This function handles the placement of cards in their new location
  const addCardToTarget = (card, targetZone, newFieldMap, newHand, newGraveyard, newBanished, newMainDeck, newExtraDeck) => {
    console.log('Adding card to target:', { card: card?.name, targetZone });
    
    if (targetZone === 'hand') {
      // Add to hand - ensure card is face-up
      const handCard = { ...card, isFaceDown: false };
      newHand.push(handCard);
      console.log('✅ Added card to hand:', card?.name);
    } else if (targetZone === 'My GYZ') {
      const cleanedCard = { ...card, isFaceDown: false };
      // Remove any materials to prevent duplication when the card is moved back to field
      delete cleanedCard.materials;
      newGraveyard.push(cleanedCard);
      console.log('✅ Added card to graveyard:', card?.name);
    } else if (targetZone === 'My BZ') {
      const cleanedCard = { ...card, isFaceDown: false };
      // Remove any materials to prevent duplication when the card is moved back to field
      delete cleanedCard.materials;
      newBanished.push(cleanedCard);
      console.log('✅ Added card to banished zone:', card?.name);
    } else if (targetZone === 'My Deck') {
      const facedownCard = { ...card, isFaceDown: true };
      newMainDeck.unshift(facedownCard);
      console.log('✅ Added card to deck:', card?.name);
    } else if (targetZone === 'My Extra') {
      const facedownCard = { ...card, isFaceDown: true };
      delete facedownCard.materials;
      newExtraDeck.push(facedownCard);
      console.log('✅ Added card to extra deck:', card?.name);
    } else {
      // Field zones - handle XYZ mechanics and regular placement
      const targetCard = newFieldMap[targetZone]?.card;
      
      if (targetCard && card?.type?.toLowerCase().includes('xyz')) {
        // XYZ summon - replace target and add as material
        console.log('🔮 XYZ summon detected');
        let absorbedMaterials = [targetCard];
        if (targetCard.type?.includes('XYZ') && Array.isArray(targetCard.materials)) {
          absorbedMaterials = [...targetCard.materials, targetCard];
        }
        
        const cleanedMaterials = absorbedMaterials.map(material => {
          const cleaned = { ...material };
          delete cleaned.materials;
          return cleaned;
        });
        
        newFieldMap[targetZone] = {
          card: {
            ...card,
            isFaceDown: false,
            materials: [...(card.materials || []), ...cleanedMaterials],
          },
          position: card.position || 'ATK',
          from: 'field',
        };
        console.log('✅ XYZ summon completed in zone:', targetZone);
      } else if (targetCard?.type?.toLowerCase().includes('xyz')) {
        // Add as material to existing XYZ monster
        console.log('Adding card as XYZ material');
        const cleanedCard = { ...card };
        delete cleanedCard.materials;
        
        newFieldMap[targetZone] = {
          ...newFieldMap[targetZone],
          card: {
            ...targetCard,
            materials: [...(targetCard.materials || []), cleanedCard],
          },
        };
        console.log('✅ Card added as XYZ material in zone:', targetZone);
      } else {
        // Regular placement
        newFieldMap[targetZone] = {
          card: {
            ...card,
            isFaceDown: false,
          },
          position: card.position || 'ATK',
          from: 'field',
        };
        console.log('✅ Card placed in field zone:', targetZone);
      }
    }
  };

  // Handle drag over - Track when cards are dragged over different zones
  const handleDragOver = (event) => {
    if (!event) return;
    
    const { active, over } = event;
    if (over && activeCard && active) {
      // Update card position for drag preview
      const activatorEvent = event.activatorEvent;
      if (activatorEvent) {
        console.log('🖱️ Drag over event:', {
          activeId: active?.id,
          overId: over?.id,
          overData: over?.data?.current,
          coordinates: {
            clientX: activatorEvent.clientX,
            clientY: activatorEvent.clientY
          }
        });
        setActiveCard({ 
          ...activeCard, 
          x: activatorEvent.clientX || 0, 
          y: activatorEvent.clientY || 0 
        });
      }
    }
  };

  // Handle background click to close panels
  const handleBackgroundClick = (e) => {
    console.log('🖱️ Background click detected on:', e.target.className);
    // Only close panels if clicking on the background, not on panels or their content
    if (e.target.classList.contains('duel-container') || 
        e.target.classList.contains('duel-inner') ||
        e.target.classList.contains('duel-field-grid')) {
      console.log('🔒 Background click on main container, closing panels');
      closeAllPanels();
    } else {
      console.log('🔒 Background click on other element, not closing panels');
    }
  };
  const { roomId } = useParams();
  const [highlightedHandCard, setHighlightedHandCard] = useState(null);
  const [showDeckView, setShowDeckView] = useState(false);
  const [userDecks, setUserDecks] = useState([]);
  const [selectedDeckId, setSelectedDeckId] = useState(null);
  const [graveyard, setGraveyard] = useState([]);
  const [showGYView, setShowGYView] = useState(false);
  const [showExtraDeckView, setShowExtraDeckView] = useState(false);
  const [banished, setBanished] = useState([]);
  const [showBZView, setShowBZView] = useState(false);
  
    // Expandable panel states
  const [showDeckPanel, setShowDeckPanel] = useState(false);
  const [showExtraDeckPanel, setShowExtraDeckPanel] = useState(false);
  const [showGYPanel, setShowGYPanel] = useState(false);
  const [showBZPanel, setShowBZPanel] = useState(false);
  
  // Drag state for panel transparency
  const [isDraggingFromPanel, setIsDraggingFromPanel] = useState(false);
  
  // XYZ material panel state
  const [xyzMaterials, setXyzMaterials] = useState([]);
  const [xyzMaterialsZone, setXyzMaterialsZone] = useState(null); // Track which zone the materials belong to
  const [showXyzPanel, setShowXyzPanel] = useState(false);
  const [xyzPanelZone, setXyzPanelZone] = useState('');
  
  // Refs for zone positioning
  const zoneRefs = {
    'My Deck': React.useRef(null),
    'My Extra': React.useRef(null),
    'My GYZ': React.useRef(null),
    'My BZ': React.useRef(null),
    // Monster zone refs for XYZ materials panel positioning
    'My MZ1': React.useRef(null),
    'My MZ2': React.useRef(null),
    'My MZ3': React.useRef(null),
    'My MZ4': React.useRef(null),
    'My MZ5': React.useRef(null),
    'EXMZ1': React.useRef(null),
    'EXMZ2': React.useRef(null)
  };

  const syncGameState = async (updatedPartialState) => {
    if (!roomId || !playerRole) return;

    const roomRef = doc(db, 'duelRooms', roomId);

    await updateDoc(roomRef, {
      [`gameState.${playerRole}`]: {
        ...(await getDoc(roomRef)).data()?.gameState?.[playerRole],
        ...updatedPartialState,
      },
      updatedAt: serverTimestamp()
    });
  };

  const user = getAuth().currentUser;
  const [playerRole, setPlayerRole] = useState(null); // 'host' or 'guest'
  const [opponentFieldMap, setOpponentFieldMap] = useState({});
  const [opponentGY, setOpponentGY] = useState([]);
  const [opponentBZ, setOpponentBZ] = useState([]);
  const [opponentActionMessage, setOpponentActionMessage] = useState('');

  useEffect(() => {
    const auth = getAuth();
    let unsubscribe;

    const unsubscribeAuth = auth.onAuthStateChanged(async (user) => {
      if (!user || !roomId) return;

      const roomRef = doc(db, 'duelRooms', roomId);
      const snapshot = await getDoc(roomRef);
      const roomData = snapshot.data();

      if (!roomData) return;

      const isHost = roomData.hostUid === user.uid;
      const myRole = isHost ? 'host' : 'guest';
      const opponentRole = isHost ? 'guest' : 'host';

      setPlayerRole(myRole);

      // Initialize gameState if host and not set
      if (isHost && !roomData.gameState) {
        const gameState = {
          host: {
            mainDeck,
            extraDeck,
            hand,
            fieldMap,
            gy: myGYZ,
            bz: myBZ
          },
          guest: {
            mainDeck: [],
            extraDeck: [],
            hand: [],
            fieldMap: {},
            gy: [],
            bz: []
          },
          turn: 'host',
          updatedAt: serverTimestamp()
        };

        await updateDoc(roomRef, { gameState });
      }

      unsubscribe = onSnapshot(roomRef, (snap) => {
        const data = snap.data();
        if (!data?.gameState) return;

        const state = data.gameState[myRole];
        const opponentState = data.gameState[opponentRole];

        // Only update if values exist to avoid wiping good data
        if (state.mainDeck?.length > 0) setMainDeck(state.mainDeck);
        if (state.extraDeck?.length > 0) setExtraDeck(state.extraDeck);
        if (state.hand?.length > 0) setHand(state.hand);

        setFieldMap(state.fieldMap || {});
        setMyGYZ(state.gy || []);
        setMyBZ(state.bz || []);

        if (opponentState) {
          // Detect visible summon BEFORE masking
          const summon = Object.entries(opponentState.fieldMap || {}).find(
            ([zoneLabel, zoneData]) =>
              zoneLabel.includes('MZ') &&
              zoneData?.from === 'hand' &&
              !zoneData.card?.isFaceDown
          );
          if (summon) {
            setOpponentActionMessage('Opponent summoned a monster from hand!');
            setTimeout(() => setOpponentActionMessage(''), 3000);
          }

          // Mask opponent face-down cards
          const filteredFieldMap = {};
          for (const zone in opponentState.fieldMap) {
            const slot = opponentState.fieldMap[zone];
            filteredFieldMap[zone] = {
              ...slot,
              card: slot.card?.isFaceDown
                ? { name: 'Face-down', isFaceDown: true }
                : slot.card
            };
          }

          setOpponentFieldMap(filteredFieldMap);
          setOpponentGY(opponentState.gy?.map(card =>
            card.isFaceDown ? { name: 'Face-down', isFaceDown: true } : card
          ) || []);
          setOpponentBZ(opponentState.bz?.map(card =>
            card.isFaceDown ? { name: 'Face-down', isFaceDown: true } : card
          ) || []);
        }
      });
    });

    return () => {
      unsubscribeAuth();
      if (unsubscribe) unsubscribe();
    };
  }, []);

  useEffect(() => {
    const fetchDeck = async () => {
      const handTestData = JSON.parse(localStorage.getItem('handTestDeck') || 'null');
      if (handTestData) {
        setMainDeck(handTestData.main);
        setExtraDeck(handTestData.extra.map(card => ({ ...card, isFaceDown: true })));
        setSideDeck(handTestData.side);
        setHand(handTestData.hand || []);
        localStorage.removeItem('handTestDeck');
        return;
      }

      const localData = await loadDeckFromStorage();

      if (localData?.source === 'online') {
        try {
          const auth = getAuth();
          const user = auth.currentUser;
          if (!user) return;

          const deckRef = doc(db, 'users', user.uid, 'decks', localData.id);
          const snapshot = await getDoc(deckRef);
          const data = snapshot.data();

          const res = await fetch('https://db.ygoprodeck.com/api/v7/cardinfo.php');
          let cardDB = (await res.json()).data;
          cardDB = Array.from(new Map(cardDB.map(c => [String(c.id), c])).values());

          const resolve = (ids = []) =>
            ids.map(id =>
              cardDB.find(c => String(c.id) === String(id)) ||
              cardDB.find(c => c.misc_info?.alias?.includes(Number(id))) ||
              cardDB.find(c => c.card_images?.some(img => String(img.id) === String(id))) ||
              { id, name: `Unknown (${id})`, card_images: [{ image_url: 'https://via.placeholder.com/80x115?text=Unknown' }] }
            );

          setMainDeck(resolve(data.main));
          setExtraDeck(resolve(data.extra).map(card => ({ ...card, isFaceDown: true })));
          setSideDeck(resolve(data.side));
          setHand([]); // draw manually if needed

        } catch (err) {
          console.error('❌ Failed to load online deck:', err);
        }
      } else if (localData) {
        setMainDeck(localData.main);
        setExtraDeck(localData.extra.map(card => ({ ...card, isFaceDown: true })));
        setSideDeck(localData.side);
        setHand(localData.hand || []);
      }
    };

    fetchDeck();

    const fetchUserDecks = async () => {
      const auth = getAuth();
      const user = auth.currentUser;
      if (!user) return;

      const deckCollectionRef = collection(db, 'users', user.uid, 'decks');
      const snapshot = await getDocs(deckCollectionRef);
      const decks = snapshot.docs.map(doc => ({
        id: doc.id,
        name: doc.data().name || doc.id,
      }));

      setUserDecks(decks);
    };

    fetchUserDecks();
  }, []);

  const handleDeckSelect = (e) => {
    const deckId = e.target.value;
    setSelectedDeckId(deckId);

    localStorage.setItem('selectedDeck', JSON.stringify({ id: deckId, source: 'online' }));
    window.location.reload(); // reload to trigger the deck loader
  };

  const gridLayout = [
    ['Opp Deck', 'Opp GYZ', 'Opp BZ', 'My FZ', 'My Extra'],
    ['My STZ10', 'My MZ10', '', 'My MZ1', 'My STZ1'],
    ['My STZ9', 'My MZ9', 'EXMZ1', 'My MZ2', 'My STZ2'],
    ['My STZ8', 'My MZ8', 'TOKEN_BUTTON', 'My MZ3', 'My STZ3'],
    ['My STZ7', 'My MZ7', 'EXMZ2', 'My MZ4', 'My STZ4'],
    ['My STZ6', 'My MZ6', '', 'My MZ5', 'My STZ5'],
    ['Opp Extra', 'Opp FZ', 'My BZ', 'My GYZ', 'My Deck']
  ];

  // Function to get zone position for panel positioning
  const getZonePosition = (zoneLabel) => {
    for (let row = 0; row < gridLayout.length; row++) {
      for (let col = 0; col < gridLayout[row].length; col++) {
        if (gridLayout[row][col] === zoneLabel) {
          return { row, col };
        }
      }
    }
    return null;
  };

  // Calculate panel positions centered on screen
  const getPanelPosition = (zoneLabel) => {
    // Get viewport dimensions
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    const panelWidth = 470; // Wide enough for 4 cards per row (4 * 88px + gaps + padding)
    const panelHeight = 250; // Approximate panel height
    
    // Center the panel on screen
    const left = Math.max(10, (viewportWidth - panelWidth) / 2);
    const top = Math.max(10, (viewportHeight - panelHeight) / 2);
    
    console.log(`Panel position for ${zoneLabel}:`, { 
      left, 
      top, 
      viewportWidth, 
      viewportHeight, 
      panelWidth, 
      panelHeight
    });
    
    return {
      position: 'fixed', // Use fixed positioning for screen center
      left: `${left}px`,
      top: `${top}px`,
      zIndex: 1000,
      width: `${panelWidth}px`
    };
  };

  // Debug panel states
  console.log('Panel states:', {
    showDeckPanel,
    showExtraDeckPanel,
    showGYPanel,
    showBZPanel,
    showXyzPanel,
    isDraggingFromPanel
  });

  // Track panel state changes
  useEffect(() => {
    console.log('🔄 Deck panel state changed to:', showDeckPanel);
  }, [showDeckPanel]);

  useEffect(() => {
    console.log('🔄 Extra deck panel state changed to:', showExtraDeckPanel);
  }, [showExtraDeckPanel]);

  useEffect(() => {
    console.log('🔄 GY panel state changed to:', showGYPanel);
  }, [showGYPanel]);

  useEffect(() => {
    console.log('🔄 BZ panel state changed to:', showBZPanel);
  }, [showBZPanel]);

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={pointerWithin}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
      onDragOver={handleDragOver}
      // Add debugging for drop detection
      onDragOverCapture={(event) => {
        console.log('🎯 Drag over capture:', {
          active: event.active?.id,
          over: event.over?.id,
          activeData: event.active?.data.current,
          overData: event.over?.data.current
        });
      }}
    >
    <div 
      className="duel-container" 
      style={{ backgroundImage: "url('/background.png')" }} 
      onClick={handleBackgroundClick}
    >

      {/* Top Control Buttons */}
      <div className="top-controls">
        <button
          className="control-button reset-button"
          onClick={handleReset}
          title="Reset: Return all cards to deck, shuffle, and draw 5"
          disabled={mainDeck.length === 0 && extraDeck.length === 0 && hand.length === 0 && Object.keys(fieldMap).filter(zone => fieldMap[zone]?.card).length === 0}
        >
          Reset
        </button>
        <button
          className="control-button draw-button"
          onClick={handleDraw}
          title="Draw 1 card from deck"
          disabled={mainDeck.length === 0}
        >
          Draw
        </button>
        <button
          className="control-button shuffle-button"
          onClick={handleShuffle}
          title="Shuffle the deck"
          disabled={mainDeck.length === 0}
        >
          Shuffle
        </button>

      </div>

      <div className="duel-inner">
        <div className="duel-field-grid">
          {gridLayout.map((column, colIdx) => (
            <div key={colIdx} className="duel-column">
              {column.map((label, rowIdx) => {
                console.log(`🎮 Rendering zone: "${label}" at [${colIdx}, ${rowIdx}]`);
                
                if (label === 'TOKEN_BUTTON') {
                  return (
                    <div
                      key={rowIdx}
                      className="zone"
                      style={{
                        justifyContent: 'center',
                        alignItems: 'center',
                        fontWeight: 'bold',
                        cursor: 'pointer',
                        display: 'flex',
                        backgroundColor: '#2c2c2c',
                        color: 'white'
                      }}
                        onClick={createToken}
                    >
                      Token
                    </div>
                  );
                }

                if (!label || label === '') {
                  console.log(`⚠️ Empty label at [${colIdx}, ${rowIdx}], skipping`);
                  return <div key={rowIdx} className="zone empty"></div>;
                }

                console.log(`🎯 Creating ZoneSlotDrag for "${label}" with props:`, {
                  label,
                  hasFieldMap: !!fieldMap,
                  hasHand: !!hand,
                  hasMainDeck: !!mainDeck,
                  hasExtraDeck: !!extraDeck
                });
                
                return (
                  <ZoneSlotDrag
                    key={label + '-' + colIdx + '-' + rowIdx}
                    label={label}
                    fieldMap={fieldMap}
                    setFieldMap={setFieldMap}
                    hand={hand}
                    setHand={setHand}
                      setMainDeck={setMainDeck}
                    mainDeck={mainDeck}
                    extraDeck={extraDeck}
                    graveyard={graveyard}
                    setGraveyard={setGraveyard}
                    banished={banished}
                    setBanished={setBanished}
                      setExtraDeck={setExtraDeck}
                      setShowDeckPanel={setShowDeckPanel}
                      setShowExtraDeckPanel={setShowExtraDeckPanel}
                      setShowGYPanel={setShowGYPanel}
                      setShowBZPanel={setShowBZPanel}
                      setShowXyzPanel={setShowXyzPanel}
                      setXyzMaterials={setXyzMaterials}
                      setXyzMaterialsZone={setXyzMaterialsZone}
                      setXyzPanelZone={setXyzPanelZone}
                      xyzMaterials={xyzMaterials}
                      xyzMaterialsZone={xyzMaterialsZone}
                      xyzPanelZone={xyzPanelZone}
                      zoneRef={zoneRefs[label]}
                      setSelectedCard={setSelectedCard}
                      setShowCardInfoModal={setShowCardInfoModal}
                      isDragging={activeId === label}
                  />
                );
              })}
            </div>
          ))}
        </div>

          {/* Expandable Deck Panels - Positioned over their zones */}
          {showDeckPanel && (
            <div 
              className="expandable-panel-overlay" 
              style={{
                ...getPanelPosition('My Deck'),
                zIndex: isDraggingFromPanel ? -5 : 1000
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <ExpandableDeckPanel
                title="Main Deck"
                cards={mainDeck}
                from="deck"
                isExpanded={true}
                onToggle={() => setShowDeckPanel(false)}
                onCardDragStart={() => setIsDraggingFromPanel(true)}
                onCardDragEnd={() => {
                  setIsDraggingFromPanel(false);
                  // Panel closing is now handled centrally in handleDragEnd
                }}
                isDragging={isDraggingFromPanel}
                setSelectedCard={setSelectedCard}
                setShowCardInfoModal={setShowCardInfoModal}
                globalIsDragging={activeId !== null}
              />
          </div>
        )}
          
          {showExtraDeckPanel && (
            <div 
              className="expandable-panel-overlay" 
              style={{
                ...getPanelPosition('My Extra'),
                zIndex: isDraggingFromPanel ? -5 : 1000
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <ExpandableDeckPanel
                title="Extra Deck"
                cards={extraDeck.map(card => {
                  const cleanCard = { ...card };
                  delete cleanCard.isFaceDown;
                  return cleanCard;
                })}
                from="extra"
                isExpanded={true}
                onToggle={() => setShowExtraDeckPanel(false)}
                onCardDragStart={() => setIsDraggingFromPanel(true)}
                onCardDragEnd={() => {
                  setIsDraggingFromPanel(false);
                  // Panel closing is now handled centrally in handleDragEnd
                }}
                isDragging={isDraggingFromPanel}
                setSelectedCard={setSelectedCard}
                setShowCardInfoModal={setShowCardInfoModal}
                globalIsDragging={activeId !== null}
              />
          </div>
        )}

          {showGYPanel && (
            <div 
              className="expandable-panel-overlay" 
              style={{
                ...getPanelPosition('My GYZ'),
                zIndex: isDraggingFromPanel ? -5 : 1000
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <ExpandableDeckPanel
                title="Graveyard"
                cards={graveyard}
                from="GY"
                isExpanded={true}
                onToggle={() => setShowGYPanel(false)}
                onCardDragStart={() => setIsDraggingFromPanel(true)}
                onCardDragEnd={() => {
                  setIsDraggingFromPanel(false);
                  // Panel closing is now handled centrally in handleDragEnd
                }}
                isDragging={isDraggingFromPanel}
                setSelectedCard={setSelectedCard}
                setShowCardInfoModal={setShowCardInfoModal}
                globalIsDragging={activeId !== null}
              />
          </div>
        )}

          {showBZPanel && (
            <div 
              className="expandable-panel-overlay" 
              style={{
                ...getPanelPosition('My BZ'),
                zIndex: isDraggingFromPanel ? -5 : 1000
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <ExpandableDeckPanel
                title="Banished Zone"
                cards={banished}
                from="BZ"
                isExpanded={true}
                onToggle={() => setShowBZPanel(false)}
                onCardDragStart={() => setIsDraggingFromPanel(true)}
                onCardDragEnd={() => {
                  setIsDraggingFromPanel(false);
                  // Panel closing is now handled centrally in handleDragEnd
                }}
                isDragging={isDraggingFromPanel}
                setSelectedCard={setSelectedCard}
                setShowCardInfoModal={setShowCardInfoModal}
                globalIsDragging={activeId !== null}
              />
          </div>
        )}

          {/* XYZ Materials Panel */}
          {showXyzPanel && (
            <div 
              className="expandable-panel-overlay" 
              style={{
                ...getPanelPosition(xyzPanelZone),
                zIndex: isDraggingFromPanel ? -5 : 1000
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <ExpandableDeckPanel
                title="XYZ Materials"
                cards={xyzMaterials}
                from="XYZ"
                isExpanded={true}
                onToggle={() => {
                  setShowXyzPanel(false);
                  setXyzMaterialsZone(null);
                }}
                onCardDragStart={() => setIsDraggingFromPanel(true)}
                onCardDragEnd={() => {
                  setIsDraggingFromPanel(false);
                  // Panel closing is now handled centrally in handleDragEnd
                }}
                isDragging={isDraggingFromPanel}
                setSelectedCard={setSelectedCard}
                setShowCardInfoModal={setShowCardInfoModal}
                globalIsDragging={activeId !== null}
              />
              </div>
            )}

        {/* Hand Bar - Moved outside the field grid for better drop detection */}
          <DroppableHandBar
            hand={hand}
            setHand={setHand}
            setSelectedCard={setSelectedCard}
            setShowCardInfoModal={setShowCardInfoModal}
          isDragging={activeId !== null}
          />
        </div>

      {/* Drag Overlay for drag preview */}
      <DragOverlay>
        {activeId && activeCard ? (
        <div 
          className="ghost-card"
          style={{
            transform: 'scale(0.8) rotate(5deg)',
            opacity: 0.7,
            filter: 'brightness(1.2)',
            boxShadow: '0 8px 25px rgba(0, 0, 0, 0.6)',
          }}
        >
          <img
              src={activeCard.card_images?.[0]?.image_url || require('../assets/images/card-back.jpg')}
              alt={activeCard.name}
            style={{
              width: '80px',
              height: '115px',
              borderRadius: '6px',
              border: '2px solid rgba(255, 255, 255, 0.3)'
            }}
          />
        </div>
        ) : null}
      </DragOverlay>

      {/* Card Info Modal */}
      {showCardInfoModal && selectedCard && (
        <div
          className="card-search-modal-overlay"
          onClick={() => setShowCardInfoModal(false)}
        >
          <div
            className="card-search-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              className="close-card-search"
              onClick={() => setShowCardInfoModal(false)}
            >
              ✕
            </button>

            <div className="card-info-img-general" style={{ marginBottom: '16px' }}>
              <img
                onContextMenu={(e) => e.preventDefault()}
                onMouseDown={(e) => e.button === 2 && e.preventDefault()}
                onTouchStart={(e) => e.preventDefault()}
                draggable={false}
                src={selectedCard.card_images?.[0]?.image_url || require('../assets/images/card-back.jpg')}
                alt={selectedCard.name}
                className="search-card-image"
              />

              <div className="card-info-general">
                <div><strong>Name:</strong> {selectedCard.name}</div>
                {selectedCard.attribute && <div><strong>Attribute:</strong> {selectedCard.attribute}</div>}
                {selectedCard.race && <div><strong>Type:</strong> {selectedCard.race}</div>}
                {(selectedCard.level || selectedCard.rank || selectedCard.linkval) && (
                  <div>
                    <strong>{selectedCard.linkval ? 'Link Rating' : selectedCard.rank ? 'Rank' : 'Level'}:</strong>{' '}
                    {selectedCard.linkval || selectedCard.rank || selectedCard.level}
                  </div>
                )}
                {selectedCard.scale !== undefined && (
                  <div><strong>Pendulum Scale:</strong> {selectedCard.scale}</div>
                )}
                {(selectedCard.atk !== undefined || selectedCard.def !== undefined) && (
                  <div>
                    <strong>ATK/DEF:</strong> {selectedCard.atk !== undefined ? selectedCard.atk : '-'} /
                    {selectedCard.def !== undefined ? ` ${selectedCard.def}` : ' -'}
                  </div>
                )}
              </div>
            </div>

            {/* 📜 Effect text */}
            <p className="search-card-desc">
              {
                selectedCard.desc
                  ?.replace(/(?=●|\[.*?\])/g, '\n')
                  .replace(/\\n/g, '\n') || 'No description available.'
              }
            </p>
          </div>
        </div>
      )}

      <button
        className="back-button-duel"
        onClick={() => {
          playSound('return');
          navigate('/deckbuilder');
        }}
      >
        {t('cardSearch.back')}
      </button>
    </div>
    </DndContext>
  );
};

export default DuelDragPage;
